import { useState } from "react";
import { ApiError } from "../lib/api";

/**
 * Confirm-gated action button used by the invitations + guest-links tables (reissue email, delete
 * invite). The action affects a real user, so it requires an explicit confirm. Manages its own
 * idle → confirm → busy → done/error state; lives inside clickable table rows, so it stops click
 * propagation. `onDone` (if given) fires after a successful action, e.g. to drop the row.
 */
export function ReissueButton({
  onReissue,
  label = "Reissue email",
  busyLabel = "Sending…",
  doneLabel = "sent ✓",
  danger = false,
  onDone,
}: {
  onReissue: () => Promise<void>;
  label?: string;
  busyLabel?: string;
  doneLabel?: string;
  danger?: boolean;
  onDone?: () => void;
}) {
  const [state, setState] = useState<"idle" | "confirm" | "busy" | "done" | "error">("idle");
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setState("busy");
    setErr(null);
    try {
      await onReissue();
      setState("done");
      onDone?.();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : `${e}`);
      setState("error");
    }
  }

  if (state === "done") {
    return <span className="tag you" style={{ fontSize: 10 }}>{doneLabel}</span>;
  }
  if (state === "busy") {
    return <button className="btn btn-sm btn-ghost loading" disabled>{busyLabel}</button>;
  }
  if (state === "confirm") {
    return (
      <span style={{ display: "inline-flex", gap: 6 }}>
        <button className={"btn btn-sm " + (danger ? "btn-danger" : "btn-primary")} onClick={(e) => { e.stopPropagation(); void go(); }}>Confirm</button>
        <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); setState("idle"); }}>Cancel</button>
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
      <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); setState("confirm"); }}>{label}</button>
      {state === "error" && <span style={{ color: "var(--red)", fontSize: 10 }} title={err ?? ""}>failed</span>}
    </span>
  );
}
